import React from "react";
import { Box, Card, Typography, CardHeader, CardContent, Divider, Grid } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import CheckIcon from "@mui/icons-material/Check";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/InfoRounded";
import ErrorIcon from "@mui/icons-material/Error";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100%",
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
  },
  placeholderIcon: {
    marginBottom: theme.spacing(1),
  },
  textTitle: { fontWeight: "bold", marginTop: 10 },
  textContent: { fontWeight: "bold", marginTop: 30 },
  header: { textAlign: "center" },
  icon: {
    fontSize: 120,
  },
  iconSuccess: {
    fill: theme.palette.success.main,
  },
  iconError: {
    fill: theme.palette.error.main,
  },
  iconInfo: {
    fill: theme.palette.info.main,
  },
  iconWarning: {
    fill: theme.palette.warning.main,
  },
}));

type TypeResult = "success" | "info" | "error" | "warning";

type Props = {
  type?: TypeResult;
  icon?: React.ReactNode;
  title?: string | React.ReactNode;
  children?: React.ReactNode | string;
  footer?: React.ReactNode | string;
};

const titleByType = {
  success: "Success!",
  info: "Info!",
  error: "Failure!",
  warning: "Warning!",
};

const IconByType = ({ type }: { type: TypeResult }) => {
  const classes = useStyles();

  if (type === "success") {
    return <CheckIcon className={clsx(classes.icon, classes.iconSuccess)} color="error" />;
  } else if (type === "info") {
    return <InfoIcon className={clsx(classes.icon, classes.iconInfo)} color="primary" />;
  } else if (type === "warning") {
    return <WarningIcon className={clsx(classes.icon, classes.iconWarning)} color="primary" />;
  }

  return <ErrorIcon className={clsx(classes.icon, classes.iconError)} color="error" />;
};

const ResultNotification = ({ icon, children, title, type, footer }: Props) => {
  const classes = useStyles();

  return (
    <Grid container justifyContent="center">
      <Grid item md={12}>
        <Card elevation={12}>
          <CardHeader
            className={classes.header}
            title={
              <Typography variant="h3" className={classes.textTitle}>
                {type && !title ? titleByType[type] : title}
              </Typography>
            }
            data-testid="result-notification-title"
          />
          <Divider />
          <CardContent>
            <Box
              mt={3}
              mb={3}
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              data-testid="result-notification-children">
              {type && !icon ? <IconByType type={type} /> : icon}
              <Typography
                className={classes.textContent}
                align="center"
                data-testid="result-notification-text">
                {children}
              </Typography>
            </Box>
            <Box mt={3} mb={3} textAlign="center">
              {footer}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ResultNotification;
